import {
    getDuplicatedItemData,
    isNew,
    useDuplicateFormId,
} from 'services/utils/editForm'
import {useEffect, useState} from 'react'
import {useForm} from 'react-hook-form'
import {useI18n} from 'store/Store'
import {
    useCreateDanceCollectionMutation,
    useGetDanceCollectionQuery,
    useSaveDanceCollectionMutation,
} from 'store/slices/api/danceCollectionsApi'
import {useGetDancesQuery} from 'store/slices/api/dancesApi'
import {useAlert} from 'components/Display/Alert/AlertContext'
import {yupResolver} from '@hookform/resolvers/yup'
import {danceCollectionSchema} from 'services/validations/schemas/danceCollection'
import {useNavigate} from 'react-router-dom'

const useDanceCollectionForm = ({danceCollectionId}) => {
    const {i18n} = useI18n()
    const {showAlert} = useAlert()
    const navigate = useNavigate()
    const isNewDanceCollection = isNew(danceCollectionId)
    const duplicateFromId = useDuplicateFormId(danceCollectionId)
    const formDanceCollectionId = isNewDanceCollection
        ? duplicateFromId
        : danceCollectionId

    const [formError, setFormError] = useState()
    const [searchDanceInputValue, setSearchDanceInputValue] = useState('')

    const {data: danceCollection} = useGetDanceCollectionQuery(
        formDanceCollectionId,
        {skip: !formDanceCollectionId}
    )

    const [doCreateDanceCollection] = useCreateDanceCollectionMutation()
    const [doSaveDanceCollection] = useSaveDanceCollectionMutation()

    const {data: dances, isFetching: isFetchingDances} = useGetDancesQuery(
        {search: searchDanceInputValue},
        {skip: !searchDanceInputValue}
    )

    const form = useForm({
        defaultValues: danceCollection ?? {dances: []},
        mode: 'onSubmit',
        resolver: yupResolver(danceCollectionSchema),
    })
    const {handleSubmit, reset} = form

    const dancesValues = form.watch('dances') || []

    useEffect(() => {
        if (danceCollection) {
            reset(
                isNewDanceCollection
                    ? getDuplicatedItemData(danceCollection)
                    : danceCollection
            )
        }
    }, [danceCollection, isNewDanceCollection, reset])

    const onSubmit = async (values) => {
        let response

        const body = {
            ...values,
            dances: values.dances?.map((dance) => dance._id),
        }

        if (values._id) {
            response = await doSaveDanceCollection(body)
        } else {
            response = await doCreateDanceCollection(body)
        }

        if (response?.data?._id) {
            const message = i18n.t('form.savedSuccessfully', {
                itemName: i18n.t('tables.danceCollection'),
            })
            navigate(`/danceCollections/${response.data._id}`)
            showAlert(message)
        }
        if (response?.error) {
            setFormError(response.error.data.error)
        }
    }

    const submit = handleSubmit(onSubmit)

    const handleRemoveDance = (index) => {
        if (!dancesValues.length) return

        const updatedDances = [...dancesValues]
        updatedDances.splice(index, 1)
        form.setValue('dances', updatedDances, {shouldDirty: true})
    }

    const isInvalidMove = (index, newIndex) =>
        !dancesValues.length ||
        newIndex < 0 ||
        newIndex >= dancesValues.length ||
        index === newIndex

    const handleMoveDance = (index, newIndex) => {
        if (isInvalidMove(index, newIndex)) return

        const updatedDances = [...dancesValues]
        const dance = dancesValues[index]
        updatedDances.splice(index, 1)
        updatedDances.splice(newIndex, 0, dance)
        form.setValue('dances', updatedDances, {shouldDirty: true})
    }

    const isExists = (dance) =>
        dancesValues.some((item) => item._id === dance._id)

    const handleAddDance = (dance) => {
        if (!dance?._id) {
            return {added: false, reason: 'invalid'}
        }

        if (isExists(dance)) {
            return {added: false, reason: 'duplicate', dance}
        }

        const value = [...dancesValues, dance]
        form.setValue('dances', value, {shouldDirty: true})
        return {added: true, dance}
    }

    return {
        form,
        submit,
        formError,
        dancesResults: dances,
        dancesResultsLoading: isFetchingDances,
        setSearchDanceInputValue,
        searchValue: searchDanceInputValue,
        handleRemoveDance,
        handleMoveDance,
        handleAddDance,
        danceCollectionDances: dancesValues,
    }
}

export default useDanceCollectionForm
